import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { is } from "immutable";

import { ActionCreator } from "actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { selectIsMobile } from "store/selectors/window";
import { selectExitLink } from "store/selectors/profile";
import { ErrorMobileModal, ErrorModal } from "component/modal";
import { ReactComponent as DangerIcon } from "assets/icons/danger.svg";
import "containers/Error/styles.scss";

function getHeader(status: number | undefined) {
  switch (status) {
    case 503:
      return <DangerIcon />;
    default:
      return null;
  }
}

// mapStateToProps also computed `newErr` (an identical copy of `error`), never read anywhere in
// this file - dropped rather than ported as dead state.
export function Error() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("translation");
  const isMobile = useAppSelector(selectIsMobile);
  const exitLink = useAppSelector(selectExitLink);
  const rawError = useAppSelector((state) => state.error) as any;

  let error: any;
  if (rawError) {
    error = rawError.filter((e: any) => e.response);
  }

  const goToExitUrl = () => {
    dispatch(ActionCreator.fetchExit(exitLink) as any).then((response: any) => {
      if (response.url) {
        globalThis.location.href = response.url;
      } else {
        globalThis.location.reload();
      }
    });
  };

  function getButtons(status: number | undefined, key: number) {
    switch (status) {
      case 401:
        if (!exitLink) {
          return null;
        }
        return (
          <div className="w-100 center">
            <button type="button" className="btn btn-danger btn-sm" onClick={goToExitUrl}>
              {t("ok")}
            </button>
          </div>
        );
      case 503:
        return (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            {t("refresh")}
          </button>
        );
      default:
        return (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              dispatch(ActionCreator.confirmError({ key }) as any);
            }}
          >
            {t("close")}
          </button>
        );
    }
  }

  function renderDefaultMessage() {
    return (
      <div>
        <h6 className="center yellow">{t("somethingWentWrong")}</h6>
        <h6 className="center">{t("pleaseTryAgain")}</h6>
      </div>
    );
  }

  function getMessage(status: number | undefined) {
    switch (status) {
      case 401:
        return (
          <div>
            <h6 className="center yellow">{t("sessionExpired")}</h6>
            <h6 className="center">{t("loginAgain")}</h6>
          </div>
        );
      case 403:
        return (
          <div>
            <h6 className="center yellow">{t("sessionInUse")}</h6>
            <h6 className="center">{t("userAlreadyLoggedIn")}</h6>
          </div>
        );
      case 417:
        return (
          <div>
            <h6 className="center yellow">{t("sessionInUse")}</h6>
            <h6 className="center">{t("userAlreadyLoggedIn")}</h6>
          </div>
        );
      case 503:
        return (
          <div>
            <h5>{t("error503")}</h5>
            <h6>{t("tryAgain")}</h6>
          </div>
        );
      default:
        return renderDefaultMessage();
    }
  }

  // Matches componentDidUpdate exactly: runs after every render (not gated by a dependency
  // array - `error` is a fresh `.filter()` result every render, so a `[error]` dependency array
  // would just re-fire on every render anyway), relying entirely on the `is()` deep-equality
  // check below to no-op when the error list hasn't actually changed - a plain `!==` reference
  // check would miss that, the same "unwind, then explicit deep-equality" rule CLAUDE.md
  // documents for `ShowMoreFilter`'s comparable guard.
  const prevErrorRef = useRef(error);
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      prevErrorRef.current = error;
      return;
    }

    const prevError = prevErrorRef.current;
    prevErrorRef.current = error;

    if (error && !is(error, prevError)) {
      if (error.size === 0) {
        dispatch(ActionCreator.hideModal("ERROR") as any);
      } else {
        dispatch(ActionCreator.broadcastSessionExpired() as any);
        dispatch(ActionCreator.showModal("ERROR") as any);
      }
    }
  });

  if (!error || error.size === 0) {
    return null;
  }

  return (
    <div className="applicationError">
      {error.map((err: any, key: number) => {
        const EModal = isMobile ? ErrorMobileModal : ErrorModal;
        // A response without a status leaves it undefined (the default message and buttons).
        const status = err?.response?.status;

        const message = renderDefaultMessage();
        const buttons = getButtons(status, key);
        const statusMessage = getMessage(status);
        const header = getHeader(status);
        return <EModal key={key} header={header} message={statusMessage ? statusMessage : message} buttons={buttons} />;
      })}
    </div>
  );
}

export default Error;
